import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, BookOpen, MessageSquare, Wrench, LogOut, Trash2, Plus, X, Package, Pencil, ShieldCheck, History, RotateCcw, Download, Check, Search, Upload, ImagePlus, LayoutTemplate, ArrowUp, ArrowDown, Eye, EyeOff, Images, Briefcase, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";

const matchesQuery = (r, q) => {
  if (!q.trim()) return true;
  return JSON.stringify(r).toLowerCase().includes(q.trim().toLowerCase());
};
const inDateRange = (iso, from, to) => {
  if (!iso) return true;
  const d = new Date(iso);
  if (from && d < new Date(from)) return false;
  if (to) { const t = new Date(to); t.setHours(23, 59, 59, 999); if (d > t) return false; }
  return true;
};

function UpdatesTimeline({ updates, fileUrl }) {
  if (!updates || updates.length === 0) return null;
  return (
    <div className="mt-5 border-l-2 border-[#FFCC00] pl-4 space-y-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><History className="w-3.5 h-3.5" /> Updates & follow-ups</div>
      {updates.map((u, i) => (
        <div key={i} data-testid={`update-${i}`} className="text-sm">
          <div className="font-mono text-[10px] text-zinc-400">
            {u.created_at ? new Date(u.created_at).toLocaleString() : ""} · {u.source === "team" ? "Added by team" : "Customer follow-up"} · {u.file_count || 0} new photo{(u.file_count || 0) === 1 ? "" : "s"}
          </div>
          {u.description && <p className="text-zinc-800 whitespace-pre-wrap mt-0.5">{u.description}</p>}
        </div>
      ))}
    </div>
  );
}

function JobEditor({ record, endpoint, reload }) {
  const [notes, setNotes] = useState(record.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const [uploadNote, setUploadNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await api.patch(`/admin/${endpoint}/${record.id}`, { notes }); toast.success("Notes saved"); reload(); }
    catch { toast.error("Could not save notes"); }
    finally { setSavingNotes(false); }
  };
  const upload = async () => {
    const files = fileRef.current?.files;
    if (!files || files.length === 0) { toast.error("Choose at least one photo first"); return; }
    const fd = new FormData();
    fd.append("note", uploadNote);
    for (const f of files) fd.append("files", f);
    setUploading(true);
    try {
      await api.post(`/admin/${endpoint}/${record.id}/files`, fd);
      toast.success("Photos added to job"); setUploadNote("");
      if (fileRef.current) fileRef.current.value = ""; reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="mt-4 border-t border-zinc-100 pt-4 grid md:grid-cols-2 gap-4" data-testid={`job-editor-${record.id}`}>
      <div>
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Job notes</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid={`notes-${record.id}`} placeholder="Add internal notes about this job…" className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-950" />
        <button onClick={saveNotes} disabled={savingNotes} data-testid={`save-notes-${record.id}`} className="mt-2 text-sm font-semibold bg-zinc-950 text-white px-3 py-1.5 hover:bg-[#002FA7] disabled:opacity-50">{savingNotes ? "Saving…" : "Save notes"}</button>
      </div>
      <div className="bg-zinc-50 border border-zinc-200 p-3">
        <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1"><ImagePlus className="w-3.5 h-3.5" /> Add photos (e.g. extra damage / supplementary quote)</label>
        <input ref={fileRef} type="file" multiple accept="image/*,video/*" data-testid={`upload-input-${record.id}`} className="mt-2 block w-full text-xs" />
        <input value={uploadNote} onChange={(e) => setUploadNote(e.target.value)} placeholder="Optional note for these photos…" data-testid={`upload-note-${record.id}`} className="mt-2 w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-950" />
        <button onClick={upload} disabled={uploading} data-testid={`upload-btn-${record.id}`} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-3 py-1.5 hover:border-[#16A34A] hover:text-[#15803D] disabled:opacity-50"><Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photos"}</button>
      </div>
    </div>
  );
}

function JobFilterBar({ q, setQ, from, setFrom, to, setTo, placeholder, testidPrefix }) {
  const active = q || from || to;
  return (
    <div className="mt-4 flex flex-wrap items-end gap-3">
      <div className="relative flex-1 min-w-[200px]">
        <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} data-testid={`${testidPrefix}-search`} className="w-full border border-zinc-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-zinc-950" />
      </div>
      <div>
        <label className="block font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 mb-1">From</label>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${testidPrefix}-from`} className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-950" />
      </div>
      <div>
        <label className="block font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-500 mb-1">To</label>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${testidPrefix}-to`} className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-zinc-950" />
      </div>
      {active && (
        <button onClick={() => { setQ(""); setFrom(""); setTo(""); }} data-testid={`${testidPrefix}-clear`} className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-950 py-2.5"><X className="w-4 h-4" /> Clear</button>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [leads, setLeads] = useState([]);
  const [articles, setArticles] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [requests, setRequests] = useState([]);
  const [parts, setParts] = useState([]);
  const [claims, setClaims] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [jobApps, setJobApps] = useState([]);
  const [stats, setStats] = useState({ unique_visitors: 0, total_visits: 0, daily: [] });

  const load = async () => {
    try {
      const [l, a, f, sr, sp, cl, st, bl, gl, ja] = await Promise.all([
        api.get("/leads"),
        api.get("/articles"),
        api.get("/admin/feedback"),
        api.get("/admin/service-requests"),
        api.get("/spare-parts"),
        api.get("/admin/claims"),
        api.get("/admin/stats"),
        api.get("/admin/site-blocks"),
        api.get("/gallery"),
        api.get("/admin/job-applications"),
      ]);
      setLeads(l.data); setArticles(a.data); setFeedback(f.data); setRequests(sr.data); setParts(sp.data); setClaims(cl.data); setStats(st.data); setBlocks(bl.data); setGallery(gl.data); setJobApps(ja.data);
    } catch (e) {
      if (e?.response?.status === 401) nav("/admin/login");
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) { nav("/admin/login"); return; }
    load();
  }, []); // eslint-disable-line

  const logout = () => {
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };

  const qualified = leads.filter((l) => l.qualified).length;
  const newLeads = leads.filter((l) => l.status === "new").length;

  return (
    <div className="flex min-h-screen bg-[#FDFDFD]">
      {/* Sidebar */}
      <aside className="w-64 bg-zinc-950 text-zinc-400 fixed h-screen flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <div className="font-display font-black text-white tracking-tight leading-tight">NOMAD<br/>CARAVAN</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Admin Control</div>
        </div>
        <nav className="flex-1 py-4">
          {[
            {k:"overview", l:"Overview", I: LayoutDashboard},
            {k:"leads", l:"Leads", I: Users},
            {k:"requests", l:"Service Requests", I: Wrench},
            {k:"claims", l:"Insurance Claims", I: ShieldCheck},
            {k:"parts", l:"Spare Parts", I: Package},
            {k:"content", l:"Page Content", I: LayoutTemplate},
            {k:"gallery", l:"Gallery", I: Images},
            {k:"jobs", l:"Job Applications", I: Briefcase},
            {k:"articles", l:"Troubleshooting", I: BookOpen},
            {k:"feedback", l:"Feedback", I: MessageSquare},
          ].map(({k,l,I}) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-nav-${k}`}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors ${
                tab === k ? "text-white bg-zinc-900 border-r-2 border-[#002FA7]" : "hover:text-white hover:bg-zinc-900"
              }`}
            >
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="admin-logout" className="m-4 flex items-center gap-2 text-sm text-zinc-500 hover:text-white">
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </aside>

      {/* Main */}
      <main className="ml-64 flex-1 p-10 max-w-full overflow-x-hidden">
        {tab === "overview" && (
          <Overview leads={leads} qualified={qualified} newLeads={newLeads} articles={articles} requests={requests} claims={claims} feedback={feedback} jobApps={jobApps} siteStats={stats} setTab={setTab} />
        )}
        {tab === "leads" && <Leads leads={leads} reload={load} />}
        {tab === "requests" && <ServiceRequests requests={requests} reload={load} />}
        {tab === "claims" && <ClaimsPanel claims={claims} reload={load} />}
        {tab === "parts" && <SparePartsPanel parts={parts} reload={load} />}
        {tab === "content" && <ContentPanel blocks={blocks} reload={load} />}
        {tab === "gallery" && <GalleryPanel images={gallery} reload={load} />}
        {tab === "jobs" && <JobsPanel apps={jobApps} reload={load} />}
        {tab === "articles" && <Articles articles={articles} reload={load} />}
        {tab === "feedback" && <FeedbackPanel feedback={feedback} reload={load} />}
      </main>
    </div>
  );
}

function Overview({ leads, qualified, newLeads, articles, requests, claims, feedback, jobApps, siteStats, setTab }) {
  const recent = leads.slice(0, 5);
  const activeReqs = requests.filter((r) => !r.archived);
  const newReqs = activeReqs.filter((r) => (r.status || "new") === "new");
  const activeClaims = claims.filter((c) => !c.archived);
  const newClaims = activeClaims.filter((c) => (c.status || "new") === "new");
  const newFeedback = feedback.filter((f) => (f.status || "new") === "new");
  const newApps = (jobApps || []).filter((a) => !a.archived && (a.status || "new") === "new");
  const attentionTotal = newReqs.length + newClaims.length + newFeedback.length + newLeads + newApps.length;

  const attention = [
    { l: "New service requests", v: newReqs.length, tab: "requests", testid: "overview-new-requests" },
    { l: "New insurance claims", v: newClaims.length, tab: "claims", testid: "overview-new-claims" },
    { l: "New feedback", v: newFeedback.length, tab: "feedback", testid: "overview-new-feedback" },
    { l: "New / unread leads", v: newLeads, tab: "leads", testid: "overview-new-leads" },
    { l: "New job applications", v: newApps.length, tab: "jobs", testid: "overview-new-jobs" },
  ];
  const stats = [
    { l: "Active claims", v: activeClaims.length, c: "text-[#002FA7]" },
    { l: "Active requests", v: activeReqs.length, c: "text-[#002FA7]" },
    { l: "Qualified leads", v: qualified, c: "text-zinc-950" },
    { l: "Total leads", v: leads.length, c: "text-zinc-950" },
  ];

  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Dashboard</div>
      <h1 className="font-display text-4xl font-black tracking-tight mt-2">Today at the workshop.</h1>

      <div className="mt-8 flex items-center gap-2">
        <span className={`inline-block w-2.5 h-2.5 rounded-full ${attentionTotal > 0 ? "bg-[#16A34A] animate-pulse" : "bg-zinc-300"}`} />
        <h2 className="font-display font-bold text-lg">{attentionTotal > 0 ? `${attentionTotal} item${attentionTotal === 1 ? "" : "s"} need your attention` : "You're all caught up "}</h2>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
        {attention.map((a, i) => (
          <button
            key={i}
            onClick={() => setTab(a.tab)}
            data-testid={a.testid}
            className={`text-left p-6 border transition-colors ${a.v > 0 ? "bg-[#16A34A]/10 border-[#16A34A]/40 hover:border-[#16A34A]" : "bg-white border-zinc-200 hover:border-zinc-400"}`}
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{a.l}</div>
            <div className={`font-display font-black text-5xl tracking-tighter mt-3 ${a.v > 0 ? "text-[#15803D]" : "text-zinc-300"}`}>{a.v}</div>
            <div className="mt-2 text-xs font-semibold text-zinc-600">View →</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-zinc-200 p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{s.l}</div>
            <div className={`font-display font-black text-4xl tracking-tighter mt-3 ${s.c}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div data-testid="overview-unique-visitors" className="bg-zinc-950 text-white p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Unique site visitors</div>
          <div className="font-display font-black text-5xl tracking-tighter mt-3 text-[#FFCC00]">{(siteStats.unique_visitors ?? 0).toLocaleString()}</div>
          <div className="mt-2 text-xs text-zinc-400">Distinct devices that have visited www.ncrw.com.au</div>
        </div>
        <div data-testid="overview-total-visits" className="bg-white border border-zinc-200 p-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Total site visits</div>
          <div className="font-display font-black text-5xl tracking-tighter mt-3 text-[#002FA7]">{(siteStats.total_visits ?? 0).toLocaleString()}</div>
          <div className="mt-2 text-xs text-zinc-600">Counted once per browsing session (refreshes don't inflate it)</div>
        </div>
      </div>

      <VisitorTrend daily={siteStats.daily || []} />

      <div className="mt-10 bg-white border border-zinc-200">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200">
          <h2 className="font-display font-bold text-xl">Recent Leads</h2>
          <button onClick={() => setTab("leads")} data-testid="overview-view-leads" className="text-sm font-semibold border-b-2 border-zinc-950 pb-0.5 hover:text-[#002FA7] hover:border-[#002FA7]">View all →</button>
        </div>
        <div className="divide-y divide-zinc-200">
          {recent.length === 0 && <div className="p-8 text-sm text-zinc-500">No leads yet.</div>}
          {recent.map((l) => (
            <div key={l.id} className="p-6 flex items-center justify-between">
              <div>
                <div className="font-semibold">{l.name} <span className="text-zinc-400 font-mono text-xs ml-2">{l.email}</span></div>
                <div className="text-sm text-zinc-600 mt-1">{l.issue}</div>
              </div>
              <div className="text-right">
                <div className={`font-display font-black text-2xl ${l.qualified ? "text-[#002FA7]" : "text-zinc-400"}`}>{l.qualification_score}</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l.qualified ? "Qualified" : "Score"}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Leads({ leads, reload }) {
  const [active, setActive] = useState(null);

  const update = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    toast.success("Lead updated");
    setActive(null); reload();
  };
  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/leads/${id}`);
    toast.success("Deleted");
    setActive(null); reload();
  };

  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pipeline</div>
      <h1 className="font-display text-4xl font-black tracking-tight mt-2">Leads ({leads.length})</h1>
      <div className="mt-10 bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-left">
            <tr>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Contact</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Issue</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Score</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Status</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Source</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.id} onClick={() => setActive(l)} data-testid={`lead-row-${l.id}`} className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer">
                <td className="p-4 font-semibold">{l.name}</td>
                <td className="p-4 text-zinc-600">{l.email}<br/><span className="font-mono text-xs">{l.phone || "—"}</span></td>
                <td className="p-4 text-zinc-700 max-w-xs truncate">{l.issue}</td>
                <td className="p-4"><span className={`font-display font-black text-lg ${l.qualified ? "text-[#002FA7]" : "text-zinc-400"}`}>{l.qualification_score}</span></td>
                <td className="p-4"><span className="font-mono text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-1">{l.status}</span></td>
                <td className="p-4 font-mono text-xs text-zinc-500">{l.source}</td>
              </tr>
            ))}
            {leads.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No leads yet — leads from the AI Receptionist and contact forms will appear here.</td></tr>}
          </tbody>
        </table>
      </div>

      {active && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-stretch justify-end" onClick={() => setActive(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white h-full overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Lead detail</div>
                <h3 className="font-display font-bold text-xl mt-1">{active.name}</h3>
              </div>
              <button onClick={() => setActive(null)} data-testid="lead-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-5 text-sm">
              <Field l="Email" v={active.email} />
              <Field l="Phone" v={active.phone || "—"} />
              <Field l="Caravan" v={active.caravan_type || "—"} />
              <Field l="Issue" v={active.issue} />
              <Field l="Budget" v={active.budget_range || "—"} />
              <Field l="Urgency" v={active.urgency || "—"} />
              <Field l="Source" v={active.source} />
              <div className="border border-zinc-950 p-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Qualification Score</div>
                <div className={`font-display font-black text-5xl mt-1 ${active.qualified ? "text-[#002FA7]" : "text-zinc-400"}`}>{active.qualification_score}<span className="text-zinc-300 text-2xl">/100</span></div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                {["new","contacted","won","lost"].map((s) => (
                  <button key={s} onClick={() => update(active.id, s)} data-testid={`lead-status-${s}`} className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${active.status === s ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}>{s}</button>
                ))}
              </div>
              <button onClick={() => remove(active.id)} data-testid="lead-delete" className="mt-6 inline-flex items-center gap-2 text-red-600 text-sm hover:underline">
                <Trash2 className="w-4 h-4" /> Delete lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ l, v }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l}</div>
      <div className="mt-1 text-zinc-900">{v}</div>
    </div>
  );
}

function Articles({ articles, reload }) {
  const blank = {title:"", slug:"", category:"", summary:"", content:"", tags:""};
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title || "", slug: a.slug || "", category: a.category || "",
      summary: a.summary || "", content: a.content || "",
      tags: Array.isArray(a.tags) ? a.tags.join(", ") : (a.tags || ""),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    const payload = { ...form, tags: form.tags.split(",").map(s=>s.trim()).filter(Boolean) };
    try {
      if (editing) await api.patch(`/admin/articles/${editing}`, payload);
      else await api.post("/admin/articles", payload);
      toast.success(editing ? "Article updated" : "Article created");
      setOpen(false); setForm(blank); setEditing(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`); toast.success("Deleted"); reload();
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Knowledge Base</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Troubleshooting Articles</h1>
        </div>
        <button onClick={openNew} data-testid="article-new" className="bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002075] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> New article</button>
      </div>
      <div className="mt-10 bg-white border border-zinc-200 divide-y divide-zinc-200">
        {articles.map((a) => (
          <div key={a.id} className="p-6 flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7]">{a.category}</div>
              <div className="font-display font-bold text-lg mt-1">{a.title}</div>
              <div className="text-sm text-zinc-600 mt-1 max-w-xl">{a.summary}</div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => openEdit(a)} data-testid={`article-edit-${a.id}`} className="text-zinc-600 hover:text-[#002FA7]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(a.id)} data-testid={`article-delete-${a.id}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {articles.length === 0 && <div className="p-8 text-sm text-zinc-500">No articles yet.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-zinc-200"><h3 className="font-display font-bold text-xl">{editing ? "Edit article" : "New article"}</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <div className="p-6 space-y-4">
              {["title","slug","category","summary","tags"].map((k) => (
                <Input key={k} k={k} v={form[k]} onChange={(v) => setForm({...form, [k]:v})} testid={`art-${k}`} />
              ))}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Content</label>
                <textarea rows={8} value={form.content} onChange={(e) => setForm({...form, content:e.target.value})} data-testid="art-content" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
              </div>
              <button onClick={save} data-testid="art-save" className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#002075]">{editing ? "Save changes" : "Save article"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ServiceRequests({ requests, reload }) {
  const token = localStorage.getItem("admin_token");
  const [view, setView] = useState("active");
  const fileUrl = (path) => `${API}/admin/claims/file?path=${encodeURIComponent(path)}&auth=${token}`;
  const exportUrl = (id) => `${API}/admin/service-requests/${id}/export?auth=${token}`;
  const STATUSES = ["new", "contacted", "scheduled", "done", "lost"];
  const setStatus = async (id, status) => {
    await api.patch(`/admin/service-requests/${id}`, { status });
    toast.success("Updated"); reload();
  };
  const setArchived = async (id, archived) => {
    await api.patch(`/admin/service-requests/${id}`, archived ? { archived: true, status: "done" } : { archived: false });
    toast.success(archived ? "Marked complete & archived" : "Restored to inbox"); reload();
  };
  const remove = async (id) => {
    if (!confirm("Delete this request permanently? This cannot be undone.")) return;
    await api.delete(`/admin/service-requests/${id}`); toast.success("Deleted"); reload();
  };
  const active = requests.filter((r) => !r.archived);
  const archived = requests.filter((r) => r.archived);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const shown = (view === "active" ? active : archived).filter((r) => matchesQuery(r, q) && inDateRange(r.created_at, from, to));
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Jobs Pipeline</div>
      <h1 className="font-display text-4xl font-black tracking-tight mt-2">Service Requests</h1>
      <p className="text-sm text-zinc-600 mt-2 max-w-xl">Call-back requests submitted from the website, including any photos the customer attached.</p>
      <div className="mt-6 inline-flex border border-zinc-300">
        <button onClick={() => setView("active")} data-testid="requests-view-active" className={`px-4 py-2 text-sm font-mono uppercase tracking-[0.15em] ${view === "active" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Inbox ({active.length})</button>
        <button onClick={() => setView("archived")} data-testid="requests-view-archived" className={`px-4 py-2 text-sm font-mono uppercase tracking-[0.15em] border-l border-zinc-300 ${view === "archived" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Archived ({archived.length})</button>
      </div>
      <JobFilterBar q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} placeholder="Search name, phone, email, van, description…" testidPrefix="requests" />
      <div className="mt-6 space-y-4">
        {shown.map((r) => (
          <div key={r.id} data-testid={`service-request-${r.id}`} className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display font-bold text-lg">{r.name}</div>
                <div className="text-sm text-zinc-600 mt-1">
                  <a href={`tel:${(r.phone||'').replace(/\s/g,'')}`} className="font-semibold text-zinc-950 hover:text-[#16A34A]">{r.phone}</a>
                  {r.email ? <> · {r.email}</> : null}
                </div>
                {r.caravan_make_model_year && <div className="text-sm text-zinc-600 mt-1">Van: {r.caravan_make_model_year}</div>}
                {r.location && <div className="text-sm text-zinc-600">Location: {r.location}</div>}
                {r.preferred_time && <div className="text-sm text-zinc-600">Best time: {r.preferred_time}</div>}
                {r.expected_timeframe && <div className="text-sm text-zinc-600">Expected time frame: <span className="font-semibold text-zinc-900">{r.expected_timeframe}</span></div>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-[10px] text-zinc-400">{new Date(r.created_at).toLocaleString()}</div>
                <span className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-1">{r.status}</span>
              </div>
            </div>
            <p className="text-zinc-900 mt-4 whitespace-pre-wrap">{r.description}</p>

            {r.files && r.files.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {r.files.map((f, i) => (
                  <a key={i} href={fileUrl(f.path)} target="_blank" rel="noreferrer" data-testid={`request-photo-${r.id}-${i}`} className="block border border-zinc-200 hover:border-[#16A34A] overflow-hidden">
                    {(f.content_type || "").startsWith("image/") ? (
                      <img src={fileUrl(f.path)} alt={f.original_filename} className="w-full h-28 object-cover" />
                    ) : (
                      <div className="w-full h-28 flex items-center justify-center text-xs text-zinc-500 bg-zinc-50 p-2 text-center">{f.original_filename || "view file"}</div>
                    )}
                  </a>
                ))}
              </div>
            )}

            <UpdatesTimeline updates={r.updates} />
            <JobEditor record={r} endpoint="service-requests" reload={reload} />

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(r.id, s)} data-testid={`request-status-${r.id}-${s}`} className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${r.status === s ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}>{s}</button>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap items-center gap-2">
              {view === "active" ? (
                <button onClick={() => setArchived(r.id, true)} data-testid={`request-archive-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#16A34A] text-white px-3 py-2 hover:bg-[#15803D]"><Check className="w-4 h-4" /> Mark complete & archive</button>
              ) : (
                <button onClick={() => setArchived(r.id, false)} data-testid={`request-restore-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-3 py-2 hover:border-zinc-950"><RotateCcw className="w-4 h-4" /> Restore to inbox</button>
              )}
              <a href={exportUrl(r.id)} data-testid={`request-export-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-3 py-2 hover:border-[#002FA7] hover:text-[#002FA7]"><Download className="w-4 h-4" /> Export (.zip)</a>
              <button onClick={() => remove(r.id)} data-testid={`request-delete-${r.id}`} className="ml-auto text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="bg-white border border-zinc-200 p-8 text-sm text-zinc-500">{(q || from || to) ? "No requests match your search." : (view === "active" ? "No service requests in the inbox." : "No archived requests.")}</div>}
      </div>
    </div>
  );
}

function ClaimsPanel({ claims, reload }) {
  const token = localStorage.getItem("admin_token");
  const [view, setView] = useState("active");
  const fileUrl = (path) => `${API}/admin/claims/file?path=${encodeURIComponent(path)}&auth=${token}`;
  const exportUrl = (id) => `${API}/admin/claims/${id}/export?auth=${token}`;
  const STATUSES = ["new", "contacted", "quoted", "submitted", "approved", "done", "lost"];
  const setStatus = async (id, status) => {
    await api.patch(`/admin/claims/${id}`, { status });
    toast.success("Updated"); reload();
  };
  const editClaimNumber = async (r) => {
    const v = window.prompt("Claim / reference number for this claim:", r.claim_number || "");
    if (v === null) return;
    await api.patch(`/admin/claims/${r.id}`, { claim_number: v.trim() });
    toast.success("Claim number saved"); reload();
  };
  const setArchived = async (id, archived) => {
    await api.patch(`/admin/claims/${id}`, archived ? { archived: true, status: "done" } : { archived: false });
    toast.success(archived ? "Marked complete & archived" : "Restored to inbox"); reload();
  };
  const remove = async (id) => {
    if (!confirm("Delete this claim permanently? This cannot be undone.")) return;
    await api.delete(`/admin/claims/${id}`); toast.success("Deleted"); reload();
  };
  const active = claims.filter((c) => !c.archived);
  const archived = claims.filter((c) => c.archived);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const shown = (view === "active" ? active : archived).filter((r) => matchesQuery(r, q) && inDateRange(r.created_at, from, to));
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Insurance Pipeline</div>
      <h1 className="font-display text-4xl font-black tracking-tight mt-2">Insurance Claims</h1>
      <p className="text-sm text-zinc-600 mt-2 max-w-xl">Photo intakes submitted via the website. Follow-up submissions with a matching claim number (or insurer + rego + email) are automatically merged into the same claim below.</p>
      <div className="mt-6 inline-flex border border-zinc-300">
        <button onClick={() => setView("active")} data-testid="claims-view-active" className={`px-4 py-2 text-sm font-mono uppercase tracking-[0.15em] ${view === "active" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Inbox ({active.length})</button>
        <button onClick={() => setView("archived")} data-testid="claims-view-archived" className={`px-4 py-2 text-sm font-mono uppercase tracking-[0.15em] border-l border-zinc-300 ${view === "archived" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}>Archived ({archived.length})</button>
      </div>
      <JobFilterBar q={q} setQ={setQ} from={from} setFrom={setFrom} to={to} setTo={setTo} placeholder="Search name, insurer, claim #, rego, description…" testidPrefix="claims" />
      <div className="mt-6 space-y-4">
        {shown.map((r) => (
          <div key={r.id} data-testid={`claim-${r.id}`} className="bg-white border border-zinc-200 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display font-bold text-lg flex items-center gap-2">
                  {r.name}
                  {r.updates && r.updates.length > 0 && (
                    <span data-testid={`claim-updates-badge-${r.id}`} className="font-mono text-[10px] uppercase tracking-[0.15em] bg-[#FFCC00] text-zinc-950 px-2 py-0.5">+{r.updates.length} update{r.updates.length > 1 ? "s" : ""}</span>
                  )}
                </div>
                <div className="text-sm text-zinc-600 mt-1">
                  <a href={`tel:${(r.phone||'').replace(/\s/g,'')}`} className="font-semibold text-zinc-950 hover:text-[#16A34A]">{r.phone}</a>
                  {r.email ? <> · {r.email}</> : null}
                </div>
                <div className="text-sm text-zinc-600 mt-1 flex items-center gap-2 flex-wrap">Insurer: <span className="font-semibold text-zinc-900">{r.insurer}</span>
                  {r.claim_number ? <span>· Claim #: <span className="font-semibold text-zinc-900">{r.claim_number}</span></span> : <span className="text-amber-600 font-mono text-[10px] uppercase tracking-[0.15em]">no claim # yet</span>}
                  <button onClick={() => editClaimNumber(r)} data-testid={`claim-edit-number-${r.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#002FA7] hover:underline"><Pencil className="w-3 h-3" /> {r.claim_number ? "edit" : "add claim #"}</button>
                </div>
                {r.rego && <div className="text-sm text-zinc-600">Rego: {r.rego}</div>}
                {r.caravan_make_model_year && <div className="text-sm text-zinc-600">Van: {r.caravan_make_model_year}</div>}
                {r.van_location && <div className="text-sm text-zinc-600">Location: {r.van_location}</div>}
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-[10px] text-zinc-400">{new Date(r.created_at).toLocaleString()}</div>
                <span className="inline-block mt-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-zinc-300 px-2 py-1">{r.status}</span>
              </div>
            </div>

            <p className="text-zinc-900 mt-4 whitespace-pre-wrap">{r.description}</p>

            {r.files && r.files.length > 0 && (
              <div className="mt-4">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">All photos ({r.files.length})</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {r.files.map((f, i) => (
                    <a key={i} href={fileUrl(f.path)} target="_blank" rel="noreferrer" data-testid={`claim-photo-${r.id}-${i}`} className="block border border-zinc-200 hover:border-[#16A34A] overflow-hidden">
                      {(f.content_type || "").startsWith("image/") ? (
                        <img src={fileUrl(f.path)} alt={f.original_filename} className="w-full h-28 object-cover" />
                      ) : (
                        <div className="w-full h-28 flex items-center justify-center text-xs text-zinc-500 bg-zinc-50 p-2 text-center">{f.original_filename || "view file"}</div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            <UpdatesTimeline updates={r.updates} />
            <JobEditor record={r} endpoint="claims" reload={reload} />

            <div className="mt-5 flex flex-wrap items-center gap-2">
              {STATUSES.map((s) => (
                <button key={s} onClick={() => setStatus(r.id, s)} data-testid={`claim-status-${r.id}-${s}`} className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${r.status === s ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}>{s}</button>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-zinc-100 flex flex-wrap items-center gap-2">
              {view === "active" ? (
                <button onClick={() => setArchived(r.id, true)} data-testid={`claim-archive-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold bg-[#16A34A] text-white px-3 py-2 hover:bg-[#15803D]"><Check className="w-4 h-4" /> Mark complete & archive</button>
              ) : (
                <button onClick={() => setArchived(r.id, false)} data-testid={`claim-restore-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-3 py-2 hover:border-zinc-950"><RotateCcw className="w-4 h-4" /> Restore to inbox</button>
              )}
              <a href={exportUrl(r.id)} data-testid={`claim-export-${r.id}`} className="inline-flex items-center gap-1.5 text-sm font-semibold border border-zinc-300 px-3 py-2 hover:border-[#002FA7] hover:text-[#002FA7]"><Download className="w-4 h-4" /> Export (.zip)</a>
              <button onClick={() => remove(r.id)} data-testid={`claim-delete-${r.id}`} className="ml-auto text-red-600 hover:text-red-700 inline-flex items-center gap-1 text-sm"><Trash2 className="w-4 h-4" /> Delete</button>
            </div>
          </div>
        ))}
        {shown.length === 0 && <div className="bg-white border border-zinc-200 p-8 text-sm text-zinc-500">{(q || from || to) ? "No claims match your search." : (view === "active" ? "No insurance claims in the inbox." : "No archived claims.")}</div>}
      </div>
    </div>
  );
}

function FeedbackPanel({ feedback, reload }) {
  const TOPIC_COLORS = {
    troubleshooting: "text-[#16A34A]",
    service: "text-[#002FA7]",
    website: "text-amber-600",
    other: "text-zinc-500",
  };
  const setStatus = async (id, status) => {
    await api.patch(`/admin/feedback/${id}`, { status });
    toast.success("Updated"); reload();
  };
  const remove = async (id) => {
    if (!confirm("Delete this feedback?")) return;
    await api.delete(`/admin/feedback/${id}`); toast.success("Deleted"); reload();
  };
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Continuous Improvement</div>
      <h1 className="font-display text-4xl font-black tracking-tight mt-2">Customer Feedback ({feedback.length})</h1>
      <p className="text-sm text-zinc-600 mt-2 max-w-xl">Feedback Rosie collected from customers about the troubleshooting guides, the website and our service.</p>
      <div className="mt-10 bg-white border border-zinc-200 divide-y divide-zinc-200">
        {feedback.map((f) => (
          <div key={f.id} data-testid={`feedback-row-${f.id}`} className="p-6 flex items-start justify-between gap-4">
            <div className="flex-1">
              <div className={`font-mono text-[10px] uppercase tracking-[0.2em] ${TOPIC_COLORS[f.topic] || TOPIC_COLORS.other}`}>{f.topic} · {f.source}</div>
              <p className="text-zinc-900 mt-2">{f.message}</p>
              {f.phone && (
                <div className="text-sm text-zinc-700 mt-2">
                  📞 <a href={`tel:${f.phone.replace(/\s/g,'')}`} className="font-semibold text-zinc-950 hover:text-[#16A34A]">{f.phone}</a>
                </div>
              )}
              <div className="font-mono text-[10px] text-zinc-400 mt-2">{new Date(f.created_at).toLocaleString()}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => setStatus(f.id, f.status === "reviewed" ? "new" : "reviewed")}
                data-testid={`feedback-toggle-${f.id}`}
                className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-2 border ${f.status === "reviewed" ? "bg-zinc-950 text-white border-zinc-950" : "border-zinc-300 hover:border-zinc-950"}`}
              >
                {f.status === "reviewed" ? "Reviewed" : "Mark reviewed"}
              </button>
              <button onClick={() => remove(f.id)} data-testid={`feedback-delete-${f.id}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {feedback.length === 0 && <div className="p-8 text-sm text-zinc-500">No feedback yet — Rosie will collect it from customer chats.</div>}
      </div>
    </div>
  );
}

function SparePartsPanel({ parts, reload }) {
  const blank = { name:"", category:"Water Pumps", description:"", price:"", quantity:0, in_stock:true, always_in_stock:false };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const CATEGORIES = ["Water Pumps", "Water Fittings", "Door Handles", "Other"];

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name:p.name, category:p.category, description:p.description||"", price:p.price ?? "", quantity:p.quantity ?? 0, in_stock:p.in_stock, always_in_stock: !!p.always_in_stock });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    const payload = {
      name: form.name.trim(),
      category: form.category,
      description: form.description.trim() || null,
      price: form.price === "" || form.price === null ? null : Number(form.price),
      quantity: form.always_in_stock ? 0 : (Number(form.quantity) || 0),
      in_stock: form.always_in_stock ? true : !!form.in_stock,
      always_in_stock: !!form.always_in_stock,
    };
    try {
      if (editing) await api.patch(`/admin/spare-parts/${editing}`, payload);
      else await api.post("/admin/spare-parts", payload);
      toast.success(editing ? "Part updated" : "Part added");
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this part?")) return;
    await api.delete(`/admin/spare-parts/${id}`); toast.success("Deleted"); reload();
  };
  const toggleStock = async (p) => {
    if (p.always_in_stock) return; // always-in-stock items can't be toggled out
    await api.patch(`/admin/spare-parts/${p.id}`, {
      name:p.name, category:p.category, description:p.description, price:p.price,
      quantity:p.quantity, in_stock: !p.in_stock, always_in_stock: !!p.always_in_stock,
    });
    reload();
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Inventory</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Spare Parts ({parts.length})</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">The single, unified stock list. Everything here is public — Rosie reads this exact list and shares availability with customers. Items sold by the metre (e.g. PEX pipe) can be set to "Always in stock".</p>
        </div>
        <button onClick={openNew} data-testid="part-new" className="bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002075] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add part</button>
      </div>

      <div className="mt-10 bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-left">
            <tr>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Category</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Qty</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Price</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Stock</th>
              <th className="p-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {parts.map((p) => (
              <tr key={p.id} data-testid={`part-row-${p.id}`} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="p-4 font-semibold">{p.name}<div className="font-normal text-zinc-500 text-xs max-w-xs truncate">{p.description}</div></td>
                <td className="p-4 text-zinc-700">{p.category}</td>
                <td className="p-4 font-display font-bold">{p.always_in_stock ? <span title="Sold by the metre — always available">∞</span> : p.quantity}</td>
                <td className="p-4 text-zinc-700">{p.price != null ? `$${Number(p.price).toFixed(2)}` : "—"}</td>
                <td className="p-4">
                  {p.always_in_stock ? (
                    <span data-testid={`part-always-${p.id}`} className="font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border bg-[#002FA7] text-white border-[#002FA7]">Always in stock</span>
                  ) : (
                    <button onClick={() => toggleStock(p)} data-testid={`part-toggle-${p.id}`} className={`font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border ${p.in_stock ? "bg-[#16A34A] text-white border-[#16A34A]" : "border-zinc-300 text-zinc-500"}`}>{p.in_stock ? "In stock" : "Out"}</button>
                  )}
                </td>
                <td className="p-4">
                  <div className="flex items-center justify-end gap-3">
                    <button onClick={() => openEdit(p)} data-testid={`part-edit-${p.id}`} className="text-zinc-600 hover:text-[#002FA7]"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p.id)} data-testid={`part-delete-${p.id}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {parts.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">No spare parts yet — add your in-house stock.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-zinc-200"><h3 className="font-display font-bold text-xl">{editing ? "Edit part" : "Add part"}</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Name</label>
                <input value={form.name} onChange={(e) => setForm({...form, name:e.target.value})} data-testid="part-name" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Category</label>
                <select value={form.category} onChange={(e) => setForm({...form, category:e.target.value})} data-testid="part-category" className="mt-2 w-full border border-zinc-300 px-4 py-3 bg-white focus:outline-none focus:border-zinc-950">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Description</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({...form, description:e.target.value})} data-testid="part-description" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer bg-zinc-50 border border-zinc-200 px-4 py-3">
                <input type="checkbox" checked={form.always_in_stock} onChange={(e) => setForm({...form, always_in_stock:e.target.checked})} data-testid="part-always" className="w-4 h-4" /> Always in stock (sold by the metre — no quantity tracked, e.g. PEX pipe)
              </label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Quantity</label>
                  <input type="number" min="0" value={form.quantity} disabled={form.always_in_stock} onChange={(e) => setForm({...form, quantity:e.target.value})} data-testid="part-quantity" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950 disabled:bg-zinc-100 disabled:text-zinc-400" />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Price (optional)</label>
                  <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => setForm({...form, price:e.target.value})} data-testid="part-price" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
                </div>
              </div>
              {!form.always_in_stock && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({...form, in_stock:e.target.checked})} data-testid="part-instock" className="w-4 h-4" /> In stock
                </label>
              )}
              <button onClick={save} data-testid="part-save" className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#002075]">{editing ? "Save changes" : "Add part"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ContentPanel({ blocks, reload }) {
  const blank = { page:"home", type:"box", title:"", body:"", visible:true };
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const homeBlocks = blocks.filter((b) => b.page === "home").sort((a,b) => a.order - b.order);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b.id);
    setForm({ page:b.page, type:b.type, title:b.title || "", body:b.body || "", visible:b.visible });
    setOpen(true);
  };

  const save = async () => {
    if (!form.body.trim() && !form.title.trim()) { toast.error("Add a title or some text"); return; }
    const payload = {
      page: form.page || "home",
      type: form.type,
      title: form.title.trim() || null,
      body: form.body,
      order: editing ? (blocks.find((b) => b.id === editing)?.order ?? homeBlocks.length) : homeBlocks.length,
      visible: !!form.visible,
    };
    try {
      if (editing) await api.patch(`/admin/site-blocks/${editing}`, payload);
      else await api.post("/admin/site-blocks", payload);
      toast.success(editing ? "Block updated" : "Block added");
      setOpen(false); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this block?")) return;
    await api.delete(`/admin/site-blocks/${id}`); toast.success("Deleted"); reload();
  };
  const patchBlock = async (b, changes) => {
    await api.patch(`/admin/site-blocks/${b.id}`, {
      page:b.page, type:b.type, title:b.title, body:b.body, order:b.order, visible:b.visible, ...changes,
    });
  };
  const toggleVisible = async (b) => { await patchBlock(b, { visible: !b.visible }); reload(); };
  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= homeBlocks.length) return;
    const a = homeBlocks[idx], c = homeBlocks[target];
    await Promise.all([ patchBlock(a, { order: c.order }), patchBlock(c, { order: a.order }) ]);
    reload();
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Page Content · Home</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Home Page Blocks ({homeBlocks.length})</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">Edit the text shown on the home hero, add new boxes or text, reorder them, or hide them. Changes go live on the site. The logo, menu, Rosie button and email are fixed and stay as they are.</p>
        </div>
        <button onClick={openNew} data-testid="block-new" className="bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002075] inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Add block</button>
      </div>

      <div className="mt-10 bg-white border border-zinc-200 divide-y divide-zinc-200">
        {homeBlocks.map((b, idx) => (
          <div key={b.id} data-testid={`block-row-${b.id}`} className={`p-5 flex items-start justify-between gap-4 ${!b.visible ? "opacity-50" : ""}`}>
            <div className="flex items-start gap-3 min-w-0">
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`block-up-${b.id}`} className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30"><ArrowUp className="w-4 h-4" /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === homeBlocks.length - 1} data-testid={`block-down-${b.id}`} className="text-zinc-400 hover:text-zinc-950 disabled:opacity-30"><ArrowDown className="w-4 h-4" /></button>
              </div>
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#002FA7]">{b.type === "box" ? "Box" : "Text"}{!b.visible && " · Hidden"}</div>
                {b.title && <div className="font-display font-bold mt-1">{b.title}</div>}
                <div className="text-sm text-zinc-600 mt-1 max-w-xl whitespace-pre-wrap line-clamp-3">{b.body}</div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button onClick={() => toggleVisible(b)} data-testid={`block-visible-${b.id}`} className="text-zinc-500 hover:text-zinc-950" title={b.visible ? "Hide" : "Show"}>{b.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
              <button onClick={() => openEdit(b)} data-testid={`block-edit-${b.id}`} className="text-zinc-600 hover:text-[#002FA7]"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(b.id)} data-testid={`block-delete-${b.id}`} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {homeBlocks.length === 0 && <div className="p-8 text-sm text-zinc-500">No blocks yet — add one to show text on the home page.</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-zinc-200"><h3 className="font-display font-bold text-xl">{editing ? "Edit block" : "Add block"}</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Style</label>
                <select value={form.type} onChange={(e) => setForm({...form, type:e.target.value})} data-testid="block-type" className="mt-2 w-full border border-zinc-300 px-4 py-3 bg-white focus:outline-none focus:border-zinc-950">
                  <option value="box">Box (highlighted card)</option>
                  <option value="text">Text (plain line)</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Heading (optional)</label>
                <input value={form.title} onChange={(e) => setForm({...form, title:e.target.value})} data-testid="block-title" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
              </div>
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Text</label>
                <textarea rows={5} value={form.body} onChange={(e) => setForm({...form, body:e.target.value})} data-testid="block-body" className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm({...form, visible:e.target.checked})} data-testid="block-visible-toggle" className="w-4 h-4" /> Visible on site
              </label>
              <button onClick={save} data-testid="block-save" className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#002075]">{editing ? "Save changes" : "Add block"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function VisitorTrend({ daily }) {
  const max = Math.max(1, ...daily.map((d) => d.visits));
  const dayLabel = (iso) => {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString(undefined, { weekday: "short" });
  };
  const weekTotal = daily.reduce((s, d) => s + d.visits, 0);
  return (
    <div className="mt-4 bg-white border border-zinc-200 p-6" data-testid="overview-visitor-trend">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Last 7 days</div>
          <div className="font-display font-bold text-lg mt-1">Visitor trend</div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-3xl tracking-tighter text-[#16A34A]">{weekTotal.toLocaleString()}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">visits this week</div>
        </div>
      </div>
      <div className="flex items-end justify-between gap-2 h-40">
        {daily.map((d) => (
          <div key={d.date} data-testid={`trend-bar-${d.date}`} className="flex-1 flex flex-col items-center justify-end h-full group">
            <div className="text-xs font-display font-bold text-zinc-700 mb-1">{d.visits}</div>
            <div
              className="w-full bg-[#16A34A] hover:bg-[#15803D] transition-all rounded-t"
              style={{ height: `${Math.max(4, (d.visits / max) * 100)}%` }}
              title={`${d.visits} visits · ${d.unique} unique on ${d.date}`}
            />
            <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 mt-2">{dayLabel(d.date)}</div>
          </div>
        ))}
        {daily.length === 0 && <div className="text-sm text-zinc-500 w-full text-center self-center">No visit data yet.</div>}
      </div>
    </div>
  );
}

function GalleryPanel({ images, reload }) {
  const [uploading, setUploading] = useState(false);
  const [items, setItems] = useState(images);
  const [dragIdx, setDragIdx] = useState(null);
  const [captions, setCaptions] = useState({});
  const fileUrl = (path) => `${API}/gallery/file?path=${encodeURIComponent(path)}`;

  useEffect(() => {
    setItems(images);
    setCaptions(Object.fromEntries(images.map((i) => [i.id, i.caption || ""])));
  }, [images]);

  const onUpload = async (fileList) => {
    const arr = Array.from(fileList);
    if (arr.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      arr.slice(0, 20).forEach((f) => fd.append("files", f));
      await api.post("/admin/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Uploaded ${Math.min(arr.length, 20)} photo(s)`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Try fewer / smaller files.");
    } finally {
      setUploading(false);
    }
  };
  const remove = async (id) => {
    if (!confirm("Delete this photo?")) return;
    await api.delete(`/admin/gallery/${id}`); toast.success("Deleted"); reload();
  };
  const saveCaption = async (id) => {
    const caption = captions[id] ?? "";
    if ((images.find((i) => i.id === id)?.caption || "") === caption) return;
    try { await api.patch(`/admin/gallery/${id}`, { caption }); toast.success("Caption saved"); }
    catch { toast.error("Could not save caption"); }
  };

  const onDrop = async (dropIdx) => {
    if (dragIdx === null || dragIdx === dropIdx) { setDragIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(dropIdx, 0, moved);
    setItems(next);
    setDragIdx(null);
    try {
      await api.post("/admin/gallery/reorder", { ids: next.map((i) => i.id) });
      toast.success("Order updated");
    } catch { toast.error("Could not save order"); reload(); }
  };

  return (
    <div>
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Public Gallery</div>
          <h1 className="font-display text-4xl font-black tracking-tight mt-2">Gallery ({items.length})</h1>
          <p className="text-sm text-zinc-600 mt-2 max-w-xl">Upload photos of your work — they appear on the public /gallery page. Drag tiles to reorder, add a caption under each, or delete any you no longer want.</p>
        </div>
        <label className={`bg-[#002FA7] text-white px-5 py-3 font-semibold hover:bg-[#002075] inline-flex items-center gap-2 cursor-pointer ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="gallery-upload-label">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photos"}
          <input type="file" multiple accept="image/*" onChange={(e) => onUpload(e.target.files)} data-testid="gallery-upload-input" className="hidden" />
        </label>
      </div>

      <div className="mt-10">
        {items.length === 0 ? (
          <div className="bg-white border border-zinc-200 p-8 text-sm text-zinc-500 text-center">No photos yet — upload your first one.</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((img, idx) => (
              <div
                key={img.id}
                data-testid={`gallery-admin-${img.id}`}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(idx)}
                className={`bg-white border ${dragIdx === idx ? "border-[#002FA7] opacity-60" : "border-zinc-200"} cursor-move`}
              >
                <div className="relative group aspect-square overflow-hidden bg-zinc-100">
                  <img src={fileUrl(img.path)} alt={img.original_filename || "photo"} loading="lazy" className="w-full h-full object-cover pointer-events-none" />
                  <button
                    onClick={() => remove(img.id)}
                    data-testid={`gallery-delete-${img.id}`}
                    className="absolute top-2 right-2 bg-red-600 text-white p-2 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                    title="Delete photo"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  <span className="absolute bottom-2 left-2 bg-black/60 text-white font-mono text-[10px] px-1.5 py-0.5">#{idx + 1}</span>
                </div>
                <input
                  value={captions[img.id] ?? ""}
                  onChange={(e) => setCaptions((c) => ({ ...c, [img.id]: e.target.value }))}
                  onBlur={() => saveCaption(img.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                  placeholder="Add a caption…"
                  data-testid={`gallery-caption-${img.id}`}
                  className="w-full text-xs px-3 py-2 border-t border-zinc-200 focus:outline-none focus:bg-zinc-50"
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function JobsPanel({ apps, reload }) {
  const token = localStorage.getItem("admin_token");
  const fileUrl = (path) => `${API}/admin/job-applications/file?path=${encodeURIComponent(path)}&auth=${token}`;

  const remove = async (id) => {
    if (!confirm("Delete this application and its files?")) return;
    await api.delete(`/admin/job-applications/${id}`); toast.success("Deleted"); reload();
  };

  return (
    <div>
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Careers</div>
        <h1 className="font-display text-4xl font-black tracking-tight mt-2">Job Applications ({apps.length})</h1>
        <p className="text-sm text-zinc-600 mt-2 max-w-xl">People who submitted their interest via the /jobs page. Download their resume, cover letter and photos.</p>
      </div>

      <div className="mt-10 space-y-4">
        {apps.length === 0 && <div className="bg-white border border-zinc-200 p-8 text-sm text-zinc-500 text-center">No applications yet.</div>}
        {apps.map((app) => (
          <div key={app.id} data-testid={`job-app-${app.id}`} className="bg-white border border-zinc-200 p-6 flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display font-bold text-lg">{app.name}</div>
              <a href={`mailto:${app.email}`} className="text-sm text-[#002FA7] hover:underline">{app.email}</a>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mt-1">{new Date(app.created_at).toLocaleString()}</div>
              <div className="flex flex-wrap gap-2 mt-4">
                {app.resume && (
                  <a href={fileUrl(app.resume.path)} target="_blank" rel="noreferrer" data-testid={`job-resume-${app.id}`} className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-sm hover:border-[#002FA7]"><FileText className="w-4 h-4" /> Resume</a>
                )}
                {app.cover_letter && (
                  <a href={fileUrl(app.cover_letter.path)} target="_blank" rel="noreferrer" data-testid={`job-cover-${app.id}`} className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-sm hover:border-[#002FA7]"><FileText className="w-4 h-4" /> Cover letter</a>
                )}
                {(app.photos || []).map((p, i) => (
                  <a key={i} href={fileUrl(p.path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-zinc-300 px-3 py-2 text-sm hover:border-[#002FA7]"><ImagePlus className="w-4 h-4" /> Photo {i + 1}</a>
                ))}
              </div>
            </div>
            <button onClick={() => remove(app.id)} data-testid={`job-delete-${app.id}`} className="text-red-600 hover:text-red-700 shrink-0"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Input({ k, v, onChange, testid }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{k}</label>
      <input value={v} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-zinc-950" />
    </div>
  );
}
